import {
  DatabaseOutlined,
  FileTextOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
  DesktopOutlined,
} from '@ant-design/icons'
import { Alert, Button, DatePicker, Drawer, Input, Select, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listAuditActors, listAuditLogs, teamErrorMessage } from './teamApi'
import {
  actorDisplayName,
  AUDIT_CATEGORIES,
  auditSourceKey,
  buildAuditParams,
  DEFAULT_AUDIT_TIMEZONE,
  defaultAuditRange,
  detailValueKind,
  formatAuditTime,
  isPlatformActor,
  productRoleOf,
  targetDisplayName,
} from './teamAudit'
import type { AuditActor, AuditCategory, AuditEvent, AuditLogResponse, AuditResult } from './teamTypes'
import { TeamAvatar } from './teamUi'

const PAGE_SIZES = [10, 20, 50]
const DEFAULT_PAGE_SIZE = 20
const RANGE_FORMAT = 'YYYY-MM-DD'

const CATEGORY_ICON: Record<AuditCategory, React.ReactNode> = {
  member: <TeamOutlined />,
  permission: <SafetyCertificateOutlined />,
  credits: <DatabaseOutlined />,
  team: <SettingOutlined />,
  system: <DesktopOutlined />,
}

/**
 * VistoeraTASK-00532 — 操作日志 (Team Security Audit Log).
 *
 * READ ONLY by design: this component has no edit / delete / clear / export
 * affordance and `teamApi` has no function that could issue such a request
 * (the backend has no such route either; the table rejects UPDATE / DELETE).
 * Server-side filtering + pagination only — the log is never loaded at once.
 */
export default function TeamAuditTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const initialRange = useMemo(() => defaultAuditRange(), [])
  const [range, setRange] = useState<{ start: string; end: string } | null>(initialRange)
  const [category, setCategory] = useState<AuditCategory | undefined>()
  const [actorId, setActorId] = useState<string | undefined>()
  const [result, setResult] = useState<AuditResult | undefined>()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [data, setData] = useState<AuditLogResponse | null>(null)
  const [actors, setActors] = useState<AuditActor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AuditEvent | null>(null)
  const reqSeq = useRef(0)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [q])

  useEffect(() => {
    let alive = true
    listAuditActors(workspaceId)
      .then((res) => alive && setActors(res.data.items))
      .catch(() => alive && setActors([]))
    return () => {
      alive = false
    }
  }, [workspaceId])

  const load = useCallback(async () => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await listAuditLogs(
        workspaceId,
        buildAuditParams({ range, actorUserId: actorId, category, result, q: debouncedQ }, page, pageSize),
      )
      if (seq !== reqSeq.current) return
      setData(res.data)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setError(teamErrorMessage(e, t('audit.loadFailed')))
    } finally {
      if (seq === reqSeq.current) setLoading(false)
    }
  }, [workspaceId, range, actorId, category, result, debouncedQ, page, pageSize, t])

  useEffect(() => {
    void load()
  }, [load])

  const tz = data?.timezone || DEFAULT_AUDIT_TIMEZONE

  const actorName = useCallback(
    (e: AuditEvent): string => {
      if (isPlatformActor(e.actor.role)) return t('audit.platformActor')
      return actorDisplayName(e) ?? t('activity.unknown')
    },
    [t],
  )
  const targetName = useCallback(
    (e: AuditEvent): string => {
      if (e.target.type === 'team') return targetDisplayName(e) ?? t('audit.teamTarget')
      return targetDisplayName(e) ?? '—'
    },
    [t],
  )
  const summary = useCallback(
    (e: AuditEvent): string =>
      t(`activity.${e.action}`, {
        actor: actorName(e),
        target: targetName(e),
        defaultValue: t('audit.otherAction', { actor: actorName(e), action: e.action }),
      }),
    [t, actorName, targetName],
  )
  const roleLabel = (role: string | null) =>
    role === 'platform_admin' || role === 'system' ? t('audit.platformActor') : role ? t(`role.${productRoleOf(role)}`) : null

  const resetFilters = () => {
    setRange(initialRange)
    setCategory(undefined)
    setActorId(undefined)
    setResult(undefined)
    setQ('')
    setDebouncedQ('')
    setPage(1)
  }

  const columns: ColumnsType<AuditEvent> = [
    { title: t('audit.col.time'), key: 'time', width: 170, render: (_: unknown, e) => <span className="tm-nowrap">{formatAuditTime(e.time, tz)}</span> },
    {
      title: t('audit.col.type'),
      key: 'type',
      width: 130,
      render: (_: unknown, e) => (
        <span className={`tm-audit-type is-${e.category}`}>
          <span className="tm-audit-type-icon">{CATEGORY_ICON[e.category]}</span>
          {t(`audit.category.${e.category}`)}
        </span>
      ),
    },
    {
      title: t('audit.col.content'),
      key: 'content',
      render: (_: unknown, e) => (
        <div className="tm-audit-content">
          <div>{summary(e)}</div>
          <div className="tm-hint tm-audit-sub">{subLine(e, t)}</div>
        </div>
      ),
    },
    {
      title: t('audit.col.actor'),
      key: 'actor',
      width: 190,
      render: (_: unknown, e) => (
        <div className="tm-member-cell">
          <TeamAvatar name={actorName(e)} size={28} round />
          <div className="tm-member-text">
            <div className="tm-audit-actor">{e.actor.email ?? actorName(e)}</div>
            {roleLabel(e.actor.role) && <div className="tm-hint">{roleLabel(e.actor.role)}</div>}
          </div>
        </div>
      ),
    },
    {
      title: t('audit.col.target'),
      key: 'target',
      width: 190,
      render: (_: unknown, e) => (
        <div className="tm-member-text">
          <div>{targetName(e)}</div>
          {e.target.email && <div className="tm-hint">{e.target.email}</div>}
        </div>
      ),
    },
    {
      title: t('audit.col.result'),
      key: 'result',
      width: 90,
      render: (_: unknown, e) => (
        <span className={`tm-tag ${e.result === 'ok' ? 'tm-tag-on' : 'tm-tag-off'}`} data-testid={`team-audit-result-${e.result}`}>
          {t(`audit.result.${e.result}`)}
        </span>
      ),
    },
    { title: t('audit.col.ip'), key: 'ip', width: 130, render: (_: unknown, e) => <span className="tm-hint">{e.ip_address ?? '—'}</span> },
    { title: t('audit.col.source'), key: 'source', width: 120, render: (_: unknown, e) => <span className="tm-hint">{t(`audit.source.${auditSourceKey(e.source)}`)}</span> },
  ]

  const stats = data?.stats
  const cards: Array<{ key: AuditCategory | 'all'; icon: React.ReactNode; value: number | undefined }> = [
    { key: 'all', icon: <FileTextOutlined />, value: stats?.total },
    ...AUDIT_CATEGORIES.map((c) => ({ key: c, icon: CATEGORY_ICON[c], value: stats?.by_category[c] })),
  ]

  const emptyText = data && data.stats.total === 0 && !category && !actorId && !result && !debouncedQ ? t('audit.emptyAll') : t('audit.empty')

  return (
    <div className="tm-audit" data-testid="team-audit">
      <div className="tm-row tm-row-audit-cards" data-testid="team-audit-cards">
        {cards.map((c) => {
          const active = c.key === 'all' ? category === undefined : category === c.key
          return (
            <button
              type="button"
              key={c.key}
              className={`tm-card tm-audit-card${active ? ' is-active' : ''}`}
              onClick={() => {
                setCategory(c.key === 'all' ? undefined : c.key)
                setPage(1)
              }}
              data-testid={`team-audit-stat-${c.key}`}
              aria-pressed={active}
            >
              <span className={`tm-audit-card-icon is-${c.key}`}>{c.icon}</span>
              <span className="tm-audit-card-text">
                <span className="tm-card-label">{t(c.key === 'all' ? 'audit.cards.all' : `audit.category.${c.key}`)}</span>
                <strong className="tm-metric-value" data-testid={`team-audit-stat-value-${c.key}`}>
                  {c.value ?? '—'}
                </strong>
                <span className="tm-hint">{t('audit.cards.inRange')}</span>
              </span>
            </button>
          )
        })}
      </div>

      <section className="tm-card">
        <div className="tm-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('audit.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 300, minWidth: 200 }}
            data-testid="team-audit-search"
          />
          <Select<AuditCategory>
            allowClear
            placeholder={t('audit.filter.type')}
            value={category}
            onChange={(v) => {
              setCategory(v)
              setPage(1)
            }}
            style={{ width: 140 }}
            options={AUDIT_CATEGORIES.map((c) => ({ value: c, label: t(`audit.category.${c}`) }))}
            data-testid="team-audit-filter-type"
          />
          <Select<string>
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            placeholder={t('audit.filter.actor')}
            value={actorId}
            onChange={(v) => {
              setActorId(v)
              setPage(1)
            }}
            style={{ width: 170 }}
            options={actors.map((a) => ({ value: a.user_id, label: a.name || a.email || a.user_id }))}
            data-testid="team-audit-filter-actor"
          />
          <Select<AuditResult>
            allowClear
            placeholder={t('audit.filter.result')}
            value={result}
            onChange={(v) => {
              setResult(v)
              setPage(1)
            }}
            style={{ width: 130 }}
            options={[
              { value: 'ok', label: t('audit.result.ok') },
              { value: 'rejected', label: t('audit.result.rejected') },
            ]}
            data-testid="team-audit-filter-result"
          />
          <DatePicker.RangePicker
            allowClear
            format={RANGE_FORMAT}
            value={range ? [dayjs(range.start), dayjs(range.end)] : null}
            onChange={(v) => {
              setRange(v && v[0] && v[1] ? { start: v[0].format(RANGE_FORMAT), end: v[1].format(RANGE_FORMAT) } : null)
              setPage(1)
            }}
            data-testid="team-audit-range"
          />
          <div className="tm-toolbar-spacer" />
          <Button onClick={resetFilters} data-testid="team-audit-reset">
            {t('audit.reset')}
          </Button>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            title={error}
            style={{ marginBottom: 12 }}
            action={
              <Button size="small" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        )}

        <Table<AuditEvent>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={data?.items ?? []}
          locale={{ emptyText }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZES,
            showTotal: (n) => t('audit.total', { total: n }),
            onChange: (p, s) => {
              setPage(p)
              setPageSize(s)
            },
          }}
          onRow={(e) => ({ onClick: () => setSelected(e), style: { cursor: 'pointer' }, 'data-testid': `team-audit-row-${e.action}` })}
          scroll={{ x: 1180 }}
        />

        <div className="tm-audit-note" data-testid="team-audit-note">
          <LockOutlined />
          <span>
            {t('audit.note')}
            {data?.recording_since ? ` ${t('audit.since', { time: formatAuditTime(data.recording_since, tz) })}` : ''}
          </span>
        </div>
      </section>

      <Drawer open={!!selected} onClose={() => setSelected(null)} size={420} title={t('audit.drawer.title')} destroyOnHidden data-testid="team-audit-drawer">
        {selected && <AuditDetail event={selected} tz={tz} summary={summary(selected)} actorName={actorName(selected)} targetName={targetName(selected)} roleLabel={roleLabel(selected.actor.role)} />}
      </Drawer>
    </div>
  )
}

/** Second line in the content cell: the most useful safe detail. */
function subLine(e: AuditEvent, t: (k: string, o?: Record<string, unknown>) => string): string {
  const d = e.detail
  if (e.result === 'rejected') return t('activity.rejected')
  if (e.action === 'member_credit_limit_updated' && d.from !== undefined && d.to !== undefined) {
    const f = (v: unknown) => (v === 'unlimited' ? t('audit.detail.unlimited') : String(v))
    return `${f(d.from)} → ${f(d.to)}`
  }
  if (e.action === 'team_renamed' && d.from !== undefined && d.to !== undefined) return `${String(d.from)} → ${String(d.to)}`
  if ((d.from === 'editor' || d.from === 'admin') && (d.to === 'editor' || d.to === 'admin')) {
    return `${t(`role.${productRoleOf(String(d.from))}`)} → ${t(`role.${productRoleOf(String(d.to))}`)}`
  }
  if (typeof d.fields === 'string') return d.fields
  if (typeof d.field === 'string') return d.field
  return ''
}

function AuditDetail({
  event: e,
  tz,
  summary,
  actorName,
  targetName,
  roleLabel,
}: {
  event: AuditEvent
  tz: string
  summary: string
  actorName: string
  targetName: string
  roleLabel: string | null
}) {
  const { t } = useTranslation('team')
  const fmt = (key: string, value: unknown): string => {
    const kind = detailValueKind(key, value)
    if (kind === 'bool') return t(value ? 'audit.detail.yes' : 'audit.detail.no')
    if (kind === 'role') return t(`role.${productRoleOf(String(value))}`)
    if (kind === 'limit') return value === 'unlimited' ? t('audit.detail.unlimited') : String(value)
    return value === null || value === undefined || value === '' ? '—' : String(value)
  }
  const entries = Object.entries(e.detail).filter(([, v]) => v !== undefined)

  return (
    <div className="tm-drawer" data-testid="team-audit-detail">
      <div className="tm-drawer-head">
        <span className={`tm-audit-card-icon is-${e.category}`}>{CATEGORY_ICON[e.category]}</span>
        <div>
          <div className="tm-drawer-name">{summary}</div>
          <div className="tm-hint">{formatAuditTime(e.time, tz)}</div>
        </div>
        <span className={`tm-tag ${e.result === 'ok' ? 'tm-tag-on' : 'tm-tag-off'}`} style={{ marginLeft: 'auto' }}>
          {t(`audit.result.${e.result}`)}
        </span>
      </div>

      <div className="tm-section-title">{t('audit.drawer.basic')}</div>
      <dl className="tm-dl">
        <dt>{t('audit.drawer.type')}</dt>
        <dd>{t(`audit.category.${e.category}`)}</dd>
        <dt>{t('audit.drawer.actor')}</dt>
        <dd>
          {e.actor.email ?? actorName}
          {roleLabel && <span className="tm-tag tm-tag-admin" style={{ marginLeft: 8 }}>{roleLabel}</span>}
        </dd>
        <dt>{t('audit.drawer.source')}</dt>
        <dd>{t(`audit.source.${auditSourceKey(e.source)}`)}</dd>
        <dt>{t('audit.drawer.ip')}</dt>
        <dd>{e.ip_address ?? '—'}</dd>
        <dt>{t('audit.drawer.result')}</dt>
        <dd>{t(`audit.result.${e.result}`)}</dd>
      </dl>

      <div className="tm-section-title">{t('audit.drawer.content')}</div>
      <dl className="tm-dl">
        <dt>{t('audit.drawer.description')}</dt>
        <dd>{summary}</dd>
        <dt>{t('audit.drawer.target')}</dt>
        <dd>
          {targetName}
          {e.target.email && <div className="tm-hint">{e.target.email}</div>}
        </dd>
        {entries.map(([k, v]) => (
          <FragmentRow key={k} label={t(`audit.detail.key.${k}`, { defaultValue: k })} value={fmt(k, v)} />
        ))}
      </dl>

      <div className="tm-section-title">{t('audit.drawer.time')}</div>
      <dl className="tm-dl">
        <dt>{t('audit.drawer.operatedAt')}</dt>
        <dd>{formatAuditTime(e.time, tz)}</dd>
        <dt>{t('audit.drawer.timezone')}</dt>
        <dd>{tz}</dd>
      </dl>

      <div className="tm-section-title">{t('audit.drawer.other')}</div>
      <dl className="tm-dl">
        <dt>{t('audit.drawer.requestId')}</dt>
        <dd>{e.request_id ?? '—'}</dd>
        <dt>{t('audit.drawer.eventId')}</dt>
        <dd>{e.id}</dd>
      </dl>
      <Alert type="info" showIcon title={t('audit.drawer.readOnly')} />
    </div>
  )
}

function FragmentRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  )
}
